/**
 * Shared layout styles for the four app shells (admin / client / worker /
 * partner), which are structurally identical. Defined once here so a mobile fix
 * cannot land in three shells and be forgotten in the fourth.
 *
 * Three classes of bug these solve:
 *
 * 1. SAFE AREA. PWA-1 made the app `display: standalone`, which on iPhone means
 *    there is no browser chrome any more — the page starts at the physical top
 *    of the screen, so a `top: 0` sticky header renders UNDER the status bar and
 *    the notch. Landscape adds left/right insets. `env(safe-area-inset-*)`
 *    (enabled by `viewport-fit=cover` in index.html) is what reclaims that space.
 *
 * 2. FLEX OVERFLOW. A flex child defaults to `min-width: auto`, meaning it
 *    refuses to shrink below its content. The content column is `flexGrow: 1`
 *    inside a flex row, so ANY wide descendant — a table, a DataGrid, a long
 *    email — widened the column instead of scrolling inside it, pushing the
 *    whole page sideways and taking text off-screen. `minWidth: 0` is the fix,
 *    and it is why tables can then scroll within their own container.
 *
 * 3. `100vh` ON MOBILE. iOS reports `vh` against the LARGEST viewport (URL bar
 *    hidden), so `100vh` overflows by the height of the browser chrome. `100dvh`
 *    tracks the actual visible height, with a `100vh` fallback for older engines.
 */

import type { SxProps, Theme } from '@mui/material';

/** Horizontal page padding that also clears a landscape notch. */
const px = {
  xs: 'max(16px, env(safe-area-inset-left, 0px))',
  md: 'max(24px, env(safe-area-inset-left, 0px))',
};
const pr = {
  xs: 'max(16px, env(safe-area-inset-right, 0px))',
  md: 'max(24px, env(safe-area-inset-right, 0px))',
};

export const shellRootSx: SxProps<Theme> = {
  display: 'flex',
  minHeight: ['100vh', '100dvh'],
  bgcolor: 'background.default',
};

/**
 * The content column beside the drawer. `minWidth: 0` is load-bearing — without
 * it wide content escapes the viewport instead of scrolling.
 */
export function shellContentSx(drawerWidth: number): SxProps<Theme> {
  return {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    width: { md: `calc(100% - ${drawerWidth}px)` },
  };
}

/** Sticky header, padded clear of the status bar / notch. */
export const shellHeaderSx: SxProps<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: (theme: Theme) => theme.zIndex.appBar,
  bgcolor: '#FFFFFF',
  borderBottom: '1px solid',
  borderColor: 'divider',
  boxSizing: 'border-box',
  pt: 'env(safe-area-inset-top, 0px)',
  pl: px,
  pr,
  minHeight: {
    xs: 'calc(64px + env(safe-area-inset-top, 0px))',
    md: 'calc(72px + env(safe-area-inset-top, 0px))',
  },
};

/**
 * The page title. `noWrap` + ellipsis matters on a 375 px phone: the title sits
 * in a row with the language switcher, messages, bell and avatar, and a long
 * translated title (Russian and Finnish run long) otherwise shoved the actions
 * off the edge of the screen.
 */
export const shellTitleSx: SxProps<Theme> = {
  flexGrow: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const shellMainSx: SxProps<Theme> = {
  flexGrow: 1,
  minWidth: 0,
  pt: { xs: 2, md: 3 },
  pl: px,
  pr,
  // Clear the iPhone home indicator.
  pb: {
    xs: 'max(16px, env(safe-area-inset-bottom, 0px))',
    md: 'max(24px, env(safe-area-inset-bottom, 0px))',
  },
};

/** Drawer contents also start under the status bar in standalone mode. */
export const shellDrawerPaperSx = {
  pt: 'env(safe-area-inset-top, 0px)',
  pb: 'env(safe-area-inset-bottom, 0px)',
};

/** Header action cluster — tighter on a phone so it cannot crowd the title. */
export const shellActionsSpacing = { xs: 0.25, sm: 1.5 };

/**
 * UX 1.0d — the box that reserves the permanent drawer's width beside the
 * content column. It used to be a `<nav>` wrapping BOTH drawers, which (with a
 * `role="navigation"` inside each drawer) exposed up to three nested
 * navigation landmarks. It is now a plain box; the one `<nav>` is the drawer
 * content itself, and only the drawer for the current width is mounted.
 */
export function shellDrawerSlotSx(drawerWidth: number): SxProps<Theme> {
  return { width: { md: drawerWidth }, flexShrink: { md: 0 } };
}
